import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Sparkles, Target, Smile, BookOpen, ShieldCheck, ExternalLink } from 'lucide-react';
import { useT } from '../i18n';
import { api } from '../api/client';
import TemplateCard from '../components/TemplateCard';
import BackButton from '../components/BackButton';

export default function TherapeuticApproach() {
  const t = useT();
  const [templates, setTemplates] = useState([]);

  const PRINCIPLES = useMemo(() => [
    { icon: Smile, title: t.therapeuticPillar1Title, text: t.therapeuticPillar1Text },
    { icon: Target, title: t.therapeuticPillar2Title, text: t.therapeuticPillar2Text },
    { icon: Heart, title: t.therapeuticPillar3Title, text: t.therapeuticPillar3Text },
    { icon: Sparkles, title: t.therapeuticPillar4Title, text: t.therapeuticPillar4Text },
  ], [t]);

  const RESEARCH = useMemo(() => [
    {
      title: t.therapeuticResearch1Title,
      source: t.therapeuticResearch1Source,
      url: 'https://ar.wikipedia.org/wiki/علاج_بالقراءة',
      summary: t.therapeuticResearch1Summary,
    },
    {
      title: t.therapeuticResearch2Title,
      source: t.therapeuticResearch2Source,
      url: 'https://sites.qsm.ac.il/qsmcourses/?page_id=2445',
      summary: t.therapeuticResearch2Summary,
    },
    {
      title: t.therapeuticResearch3Title,
      source: t.therapeuticResearch3Source,
      url: 'https://sites.google.com/site/3elajbelqessa/القصة-العلاجية',
      summary: t.therapeuticResearch3Summary,
    },
    {
      title: t.therapeuticResearch4Title,
      source: t.therapeuticResearch4Source,
      url: 'https://arttherapykw.com/الببليوثيرابيا-أو-العلاج-بالقراءة/',
      summary: t.therapeuticResearch4Summary,
    },
  ], [t]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get('/api/templates?category=therapy')
      .then((d) => setTemplates(d.templates || []))
      .catch(() => {});
  }, []);

  return (
    <div className="article-page">
      <BackButton fallback="/" />

      <header className="article-hero">
        <span className="article-hero-icon"><Heart size={30} /></span>
        <h1 className="article-title">{t.therapeuticPageTitle}</h1>
        <p className="article-lede">{t.therapeuticLede}</p>
      </header>

      <section className="article-section">
        <h2 className="article-heading">{t.therapeuticPrinciplesTitle}</h2>
        <p className="article-text">{t.therapeuticPrinciplesIntro}</p>
        <div className="principle-grid">
          {PRINCIPLES.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="principle-card">
                <span className="principle-icon"><Icon size={22} /></span>
                <h3 className="principle-title">{p.title}</h3>
                <p className="principle-text">{p.text}</p>
              </div>
            );
          })}
        </div>
        <p className="article-note">{t.therapeuticPrinciplesNote}</p>
      </section>

      <section className="article-section">
        <h2 className="article-heading">{t.therapeuticScienceTitle}</h2>
        <p className="article-text">{t.therapeuticScienceIntro}</p>
        <ul className="article-list">
          <li>{t.therapeuticScienceIdentification}</li>
          <li>{t.therapeuticScienceCatharsis}</li>
          <li>{t.therapeuticScienceInsight}</li>
        </ul>
        <p className="article-text">{t.therapeuticScienceSourcesIntro}</p>
        <div className="research-list">
          {RESEARCH.map((r) => (
            <a key={r.url} href={r.url} target="_blank" rel="noopener noreferrer" className="research-link">
              <span className="research-link-icon"><BookOpen size={20} /></span>
              <span className="research-link-body">
                <span className="research-link-title">{r.title}</span>
                <span className="research-link-summary">{r.summary}</span>
                <span className="research-link-source">{r.source}</span>
              </span>
              <ExternalLink size={16} className="research-link-arrow" aria-hidden="true" />
            </a>
          ))}
        </div>
      </section>

      <section className="article-section">
        <div className="article-disclaimer">
          <ShieldCheck size={22} className="article-disclaimer-icon" aria-hidden="true" />
          <p>{t.therapeuticDisclaimer}</p>
        </div>
      </section>

      <section className="article-section">
        <h2 className="article-heading">{t.therapeuticCtaTitle}</h2>
        <p className="article-text">{t.therapeuticCtaIntro}</p>
        {templates.length > 0 && (
          <div className="template-grid">
            {templates.map((tp) => <TemplateCard key={tp._id} template={tp} />)}
          </div>
        )}
        <div className="article-cta">
          <Link to="/c/therapy" className="submit-btn">{t.therapeuticCtaBtn}</Link>
        </div>
      </section>
    </div>
  );
}
